import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import {
  ShippingAppOptionsResponseDto,
  ShippingAppResponseDto,
} from '@tiendanube/common';
import { FEATURE_NE_PREMIUM_DELIVERY_OPTIONS } from 'App/features';
import { useNavegate } from 'App/hooks';
import { ErrorScreen } from 'commons/components';
import { useForm, useToastStatus } from 'commons/hooks';
import { NE_PREMIUM_DELIVERY_OPTIONS_SOURCE } from 'config/upsellFlowSources';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import { useStoreInfo } from 'domains/PartnersApps/hooks';
import { NE_PREMIUM_DELIVERY_OPTIONS } from 'domains/Shipping/DeliveryMethods/ne-premium-delivery-options';
import { trackingShippingModalities } from 'domains/Shipping/tracking';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import NuvemEnvioModals from './NuvemEnvioModals';
import useGetDetailActiveApp from './useGetDetailActiveApp';
import useUpdateDetailActiveApp from './useUpdateDetailActiveApp';
import { SHIPPING_METHODS_BASE_ROUTE } from '../../shippingRoutes';
import CarriersForm from '../components/CarriersForm';
import { createShippingModalityValidationSchema } from '../components/ShippingModalityCard';
import ShippingPage from '../components/ShippingPage';

const mock = {};

function EditCarriersPage() {
  const t = useTranslationShipping();
  const { goTo, getQueryParam, goBack } = useNavegate();
  const { id } = useParams<{ id: string }>();
  const { id: storeId } = useStoreInfo();
  const { detailActiveApp, fetchDetailActiveApp, isError, isLoading } =
    useGetDetailActiveApp();

  const { updateDetailActive, status } = useUpdateDetailActiveApp();

  const sortNuvemEnvio = (
    response: ShippingAppResponseDto,
  ): Record<string, ShippingAppOptionsResponseDto> => {
    if (response.name !== 'Nuvem Envio') return response.options;

    const {
      'ne-loggi-economic': loggiEconomic,
      'ne-loggi-express': loggiExpress,
      'ne-loggi-economic-collect': loggiEconomicCollect,
      'ne-jadlog-package': jadlogPackage,
      'ne-jadlog-com': jadlogCom,
      ...otherOptions
    } = response.options;

    return {
      ...(loggiEconomic && { 'ne-loggi-economic': loggiEconomic }),
      ...(loggiExpress && { 'ne-loggi-express': loggiExpress }),
      ...(loggiEconomicCollect && {
        'ne-loggi-economic-collect': loggiEconomicCollect,
      }),
      ...(jadlogPackage && { 'ne-jadlog-package': jadlogPackage }),
      ...(jadlogCom && { 'ne-jadlog-com': jadlogCom }),
      ...otherOptions,
    };
  };

  const data = useMemo(() => {
    if (detailActiveApp?.options) {
      return sortNuvemEnvio(detailActiveApp);
    }
    return mock;
  }, [detailActiveApp]);

  const modalityCodes = useMemo(() => Object.keys(data ?? {}), [data]);
  const validationSchema = useMemo(
    () => createShippingModalityValidationSchema(modalityCodes),
    [modalityCodes],
  );

  const { values, handleOnSubmit, handleChangeGroup, errors } = useForm({
    initialValues: data,
    validationSchema,
    onSubmit: async (data) => {
      if (data) {
        await updateDetailActive(id, data);

        if (detailActiveApp?.name) {
          const options = Object.values(data).map((option) => {
            const typedOption = option as ShippingAppOptionsResponseDto;

            return {
              code: typedOption.code,
              isEnabled: typedOption.isEnabled,
            };
          });

          trackingShippingModalities(
            storeId,
            detailActiveApp.name,
            JSON.stringify(options),
          );
        }
        const fromOnboarding = getQueryParam('from') === 'onBoarding';

        if (fromOnboarding) {
          goTo(SHIPPING_METHODS_BASE_ROUTE);
        } else {
          goBack();
        }
      }
    },
  });

  const transformedErrors = Object.keys(errors || {}).reduce((acc, key) => {
    if (key.includes('.') && errors[key]) {
      const [modalityCode, field] = key.split('.');
      if (!acc[modalityCode]) {
        acc[modalityCode] = {};
      }
      acc[modalityCode][field] = errors[key]!;
    }
    return acc;
  }, {} as Record<string, Record<string, string>>);

  useToastStatus({
    status,
    success: t('carriers.successToast'),
    error: t('carriers.errorToast'),
  });

  const handleOnFetchDetailActiveApp = useCallback(() => {
    fetchDetailActiveApp(id);
  }, [fetchDetailActiveApp, id]);

  useEffect(() => {
    handleOnFetchDetailActiveApp();
  }, [handleOnFetchDetailActiveApp]);

  const triggerUpsellFlow = useUpsellFlow({
    featureKey: FEATURE_NE_PREMIUM_DELIVERY_OPTIONS,
    trackingSource: NE_PREMIUM_DELIVERY_OPTIONS_SOURCE,
    title: t(
      'deliveryMethods.activeShippingMethods.editPage.modalitiesCard.nePremiumDeliveryOptions.upsellTitle',
    ),
    callback: (group: string, changeEvent: ChangeEvent<HTMLInputElement>) => {
      handleChangeGroup(group, changeEvent);
    },
  });

  const handleChangeGroupWithUpsell = useCallback(
    (
      deliveryOptionCode: string,
      changeEvent: ChangeEvent<HTMLInputElement>,
    ) => {
      const isTryingToEnableDeliveryOption =
        changeEvent.target.name === 'isEnabled' && changeEvent.target.checked;
      const isNePremiumDeliveryOption =
        NE_PREMIUM_DELIVERY_OPTIONS.includes(deliveryOptionCode);
      const shouldUseUpsellFlow =
        isTryingToEnableDeliveryOption && isNePremiumDeliveryOption;
      if (shouldUseUpsellFlow) {
        triggerUpsellFlow(deliveryOptionCode, changeEvent);
      } else {
        handleChangeGroup(deliveryOptionCode, changeEvent);
      }
    },
    [handleChangeGroup, triggerUpsellFlow],
  );

  return (
    <ShippingPage
      isError={isError}
      handleSave={handleOnSubmit}
      title={isLoading ? 'skeleton' : detailActiveApp?.name ?? ''}
      isLoading={isLoading}
      isUpdating={status === 'loading'}
    >
      {isError && (
        <ErrorScreen
          description={t('genericError.text')}
          onRetry={handleOnFetchDetailActiveApp}
        />
      )}
      {isLoading && <CarriersForm.Skeleton />}
      {detailActiveApp && !isLoading && !isError && (
        <CarriersForm
          values={values}
          handleOnChangeGroup={handleChangeGroupWithUpsell}
          isCompatibleWithMultiCD={detailActiveApp.isMultiCDCompatible}
          errors={transformedErrors}
        />
      )}

      {detailActiveApp?.name === 'Nuvem Envio' && !isLoading && (
        <NuvemEnvioModals detailActiveApp={detailActiveApp} />
      )}
    </ShippingPage>
  );
}

export default EditCarriersPage;
